import { useCallback, useEffect } from 'react';
import { useChannelStateContext } from 'stream-chat-react';

import { useAIAssistantStatus } from 'src/features/assistant/useAIAssistantStatus';
import { ChannelContent } from 'src/features/channel/channel-content/ChannelContent';
import { useTypingStatus } from 'src/features/messages/useTypingStatus';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { clearStatus } from 'src/store/aiAssistantStatus/slice';
import {
  AssistantUserID,
  selectAllCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';

interface InternalChatContentProps {
  placeholderText?: string;
  messageId?: string;
  isInternalChat?: boolean;
  setAIAssistantStatus?: (
    channelId: string,
    status: string,
    metadata?: { tool?: string; query?: string },
  ) => void;
  clearAIAssistantStatus?: () => void;
}

/**
 * Content component that must be rendered inside a ChannelProvider.
 * It retrieves AI assistant and typing status and renders ChannelContent.
 */
export const InternalChatContent = (props: InternalChatContentProps) => {
  const dispatch = useAppDispatch();
  const { channel } = useChannelStateContext();

  // Get status data using hooks - these work because we're inside ChannelProvider
  const { status: aiAssistantStatus, cancelStream } = useAIAssistantStatus();
  const typingStatus = useTypingStatus();

  // Get entity data from Redux for name resolution
  const clientsMap = useAppSelector(selectClientsMap);
  const companiesMap = useAppSelector(selectAllCompaniesMap);

  // Clear AI status when assistant message appears in chat
  useEffect(() => {
    if (!channel?.state?.messages) return;

    const messages = Object.values(channel.state.messages);
    const latestMessage = messages[messages.length - 1];

    // Check if latest message is from assistant
    if (latestMessage?.user?.id === AssistantUserID) {
      // AI responded - safe to clear status now
      dispatch(clearStatus({ channelId: channel.cid }));
    }
  }, [channel?.state?.messages, channel?.cid, dispatch]);

  // Create lookup functions for entity name resolution
  const getCompanyFromId = useCallback(
    (companyId: string) => {
      const company = companiesMap.get(companyId);
      if (!company) return null;
      return { name: company.fields.name };
    },
    [companiesMap],
  );

  const getClientFromId = useCallback(
    (clientId: string) => {
      const client = clientsMap.get(clientId);
      if (!client) return null;
      // Combine given name and family name for display
      const name =
        [client.fields.givenName, client.fields.familyName]
          .filter(Boolean)
          .join(' ') || client.fields.email;
      return { name };
    },
    [clientsMap],
  );

  return (
    <ChannelContent
      {...props}
      aiAssistantStatus={aiAssistantStatus}
      typingStatus={typingStatus}
      getCompanyFromId={getCompanyFromId}
      getClientFromId={getClientFromId}
      cancelAIStream={cancelStream}
    />
  );
};
